/**
 * ACU Persistence Bridge - Event-driven ACU usage persistence to Supabase.
 *
 * ARCHITECTURE:
 *   computeStore.consumeACU() → eventBus("compute:acu_consumed")
 *     → ACUPersistenceBridge
 *       → ACUUsageLedger.nextSequence()  [synchronous, localStorage]
 *       → ACUOfflineJournal.append()     [synchronous, localStorage]
 *       → supabase.acu_usage_history.upsert() [async, idempotent via sequence_num]
 *
 * LOCAL vs CLOUD:
 *   Local writes are SYNCHRONOUS (sequence mint + journal append happen in the
 *   event handler, before any awaited operation).
 *   Cloud sync is ASYNCHRONOUS with exponential backoff retry (3 attempts).
 *
 * CRASH RECOVERY:
 *   On module init, any journal entries with syncedAt = null are re-enqueued.
 *   The cloud upsert is idempotent: UNIQUE(user_id, sequence_num) means a retry
 *   with the same sequence_num is a no-op if already persisted.
 *
 * OFFLINE / UNAUTHENTICATED:
 *   If auth.getUser() returns no user, the event is written to the journal but
 *   NOT queued for cloud sync. Entries are replayed on the next module init
 *   (next page load) once auth is available.
 *
 * LIFECYCLE (as RuntimeModule):
 *   init():     Subscribe to compute:acu_consumed, replay unsynced journal
 *   teardown(): Flush pending entries (best-effort), remove listeners
 */

import { eventBus } from "@/runtime/eventBus";
import { logger } from "@/system/logger";
import { supabase as supabaseTyped } from "@/integrations/supabase/client";
import { persistSave, persistLoad } from "./persistenceEngine";
import { ACUUsageLedger } from "./acuUsageLedger";
import type { RuntimeModule } from "@/runtime/coreRuntime";

/**
 * Untyped Supabase client alias for tables not yet reflected in generated types.
 * acu_usage_history predates the current type-generation setup.
 * See: supabase/migrations/20260403234624_add_acu_tracking_system.sql
 */
const supabase = supabaseTyped as unknown as {
  auth: typeof supabaseTyped.auth;
  from(table: string): {
    upsert(
      values: Record<string, unknown>,
      options?: Record<string, unknown>
    ): Promise<{ data: null; error: { message?: string } | null }>;
  };
};

/* =========================================================
   OFFLINE JOURNAL
   Append-only per-user array of ACU usage entries, stored in localStorage.
   Each entry has a syncedAt timestamp (null = not yet synced).
   ========================================================= */

const JOURNAL_KEY_PREFIX = "acu-usage-journal-";

interface ACUJournalEntry {
  id:           string;
  userId:       string;
  sequenceNum:  number;
  operation:    string;
  operationId:  string;
  acuConsumed:  number;
  computeMode:  string;
  projectId?:   string;
  timestamp:    number;
  savedAt:      number;
  syncedAt:     number | null;
  attempts:     number;
  lastError:    string | null;
}

function journalKey(userId: string): string {
  return `${JOURNAL_KEY_PREFIX}${userId}`;
}

function readJournal(userId: string): ACUJournalEntry[] {
  return persistLoad<ACUJournalEntry[]>(journalKey(userId)) ?? [];
}

function writeJournal(userId: string, entries: ACUJournalEntry[]): void {
  persistSave(journalKey(userId), entries);
}

const ACUOfflineJournal = {
  append(entry: ACUJournalEntry): void {
    const entries = readJournal(entry.userId);
    entries.push(entry);
    writeJournal(entry.userId, entries);
  },

  markSynced(userId: string, sequenceNum: number): void {
    const entries = readJournal(userId);
    const idx = entries.findIndex(e => e.sequenceNum === sequenceNum);
    if (idx >= 0) {
      entries[idx].syncedAt = Date.now();
      writeJournal(userId, entries);
    }
  },

  recordAttempt(userId: string, sequenceNum: number, error: string): void {
    const entries = readJournal(userId);
    const idx = entries.findIndex(e => e.sequenceNum === sequenceNum);
    if (idx >= 0) {
      entries[idx].attempts  += 1;
      entries[idx].lastError  = error;
      writeJournal(userId, entries);
    }
  },

  getAllUnsynced(userId: string): ACUJournalEntry[] {
    return readJournal(userId).filter(e => e.syncedAt === null);
  },
};

/* =========================================================
   HELPERS
   ========================================================= */

/** UUID v4 pattern - used to validate project_id before passing to Supabase UUID column. */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUUID(value: string | undefined): value is string {
  return !!value && UUID_REGEX.test(value);
}

/** Crypto-safe entry ID - deterministic from operationId + sequenceNum. */
function makeEntryId(operationId: string, sequenceNum: number): string {
  return `acu-${operationId}-seq${sequenceNum}`;
}

const BASE_DELAY_MS = 2_000;
const MAX_ATTEMPTS  = 3;

function delayMs(attempt: number): number {
  return BASE_DELAY_MS * Math.pow(4, attempt);
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/* =========================================================
   BRIDGE
   ========================================================= */

class ACUPersistenceBridgeImpl {
  private unsubs: (() => void)[] = [];
  private syncQueue: ACUJournalEntry[] = [];
  private syncInProgress = false;
  /**
   * Session token - incremented on every init(). In-flight async operations
   * capture the token at launch and bail out if it no longer matches, preventing
   * operations from a previous lifecycle from interfering with the current one.
   */
  private sessionToken = 0;

  /* ── INIT ──────────────────────────────────────────────────────── */

  init(): void {
    this.sessionToken += 1;
    this.syncQueue = [];
    this.syncInProgress = false;
    this.unsubs.push(
      eventBus.on("compute:acu_consumed", (e) => this.onACUConsumed(e)),
    );
    // Replay any unsynced entries from a previous session (crash recovery)
    void this.replayUnsynced(this.sessionToken);
    logger.info("ACUPersistenceBridge initialized");
  }

  /* ── TEARDOWN ──────────────────────────────────────────────────── */

  teardown(): void {
    // Increment session token - invalidates ALL in-flight async operations
    // from this lifecycle. They will check the token and bail out silently.
    this.sessionToken += 1;
    this.syncQueue = [];
    this.syncInProgress = false;
    this.unsubs.forEach(fn => fn());
    this.unsubs = [];
    logger.info("ACUPersistenceBridge torn down");
  }

  /* ── EVENT HANDLER ─────────────────────────────────────────────── */

  private onACUConsumed(event: {
    type:        "compute:acu_consumed";
    projectId?:  string;
    operationId: string;
    operation:   string;
    acuConsumed: number;
    computeMode: string;
    timestamp:   number;
  }): void {
    // Capture session token at event dispatch time - used to detect stale ops
    const token = this.sessionToken;
    void this.persistConsumedACU(event, token);
  }

  private async persistConsumedACU(event: {
    projectId?:  string;
    operationId: string;
    operation:   string;
    acuConsumed: number;
    computeMode: string;
    timestamp:   number;
  }, token: number): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (token !== this.sessionToken) return; // session was torn down mid-flight

    if (!user) {
      // Not authenticated - store in a temporary anonymous journal.
      // These entries will be replayed after the user authenticates and
      // the bridge is re-initialized. For now, log and skip cloud sync.
      logger.debug("ACUPersistenceBridge: no auth user - skipping cloud sync for ACU event", {
        operationId: event.operationId,
      });
      return;
    }

    const userId = user.id;

    // Mint sequence number and persist to ledger SYNCHRONOUSLY
    const sequenceNum = ACUUsageLedger.nextSequence(userId);
    const entryId     = makeEntryId(event.operationId, sequenceNum);

    // Write to offline journal SYNCHRONOUSLY
    const journalEntry: ACUJournalEntry = {
      id:          entryId,
      userId,
      sequenceNum,
      operation:   event.operation,
      operationId: event.operationId,
      acuConsumed: event.acuConsumed,
      computeMode: event.computeMode,
      projectId:   event.projectId,
      timestamp:   event.timestamp,
      savedAt:     Date.now(),
      syncedAt:    null,
      attempts:    0,
      lastError:   null,
    };

    ACUOfflineJournal.append(journalEntry);
    logger.debug("ACUPersistenceBridge: journal entry written", { userId, sequenceNum, entryId });

    // Enqueue async cloud sync - pass token so drain can detect stale session
    this.syncQueue.push(journalEntry);
    void this.drainSyncQueue(userId, token);
  }

  /* ── CLOUD SYNC ─────────────────────────────────────────────────── */

  private async drainSyncQueue(userId: string, token: number): Promise<void> {
    if (this.syncInProgress) return;
    this.syncInProgress = true;

    try {
      while (this.syncQueue.length > 0) {
        if (token !== this.sessionToken) return; // session invalidated mid-drain
        const entry = this.syncQueue.shift()!;
        await this.syncEntryWithRetry(userId, entry, token);
      }
    } finally {
      this.syncInProgress = false;
    }
  }

  private async syncEntryWithRetry(userId: string, entry: ACUJournalEntry, token: number): Promise<void> {
    const startMs = Date.now();

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      try {
        const { error } = await supabase
          .from("acu_usage_history")
          .upsert(
            {
              id:              entry.id,
              user_id:         userId,
              project_id:      isUUID(entry.projectId) ? entry.projectId : null,
              acu_spent:       entry.acuConsumed,
              usage_type:      entry.operation,
              usage_timestamp: new Date(entry.timestamp).toISOString(),
              sequence_num:    entry.sequenceNum,
              compute_mode:    entry.computeMode,
              operation_id:    entry.operationId,
            },
            { onConflict: "user_id,sequence_num" }
          );

        if (error) throw new Error(error.message ?? "Supabase upsert error");

        // SUCCESS - check session token before any side effects
        if (token !== this.sessionToken) return;
        const durationMs = Date.now() - startMs;
        ACUOfflineJournal.markSynced(userId, entry.sequenceNum);
        ACUUsageLedger.markSynced(userId, entry.sequenceNum);
        eventBus.emit({ type: "compute:usage_synced", userId, sequenceNum: entry.sequenceNum, durationMs });
        logger.info("ACUPersistenceBridge: cloud sync success", {
          userId, sequenceNum: entry.sequenceNum, durationMs, attempt: attempt + 1,
        });
        return;

      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : String(err);
        if (token !== this.sessionToken) return; // bail out before any state mutation
        ACUOfflineJournal.recordAttempt(userId, entry.sequenceNum, errorMsg);

        const isLastAttempt = attempt === MAX_ATTEMPTS - 1;
        const nextDelay     = delayMs(attempt);

        if (isLastAttempt) {
          ACUUsageLedger.markFailed(userId, errorMsg);
          eventBus.emit({
            type:          "compute:sync_exhausted",
            userId,
            sequenceNum:   entry.sequenceNum,
            totalAttempts: MAX_ATTEMPTS,
          });
          logger.error("ACUPersistenceBridge: sync exhausted after max attempts", {
            userId, sequenceNum: entry.sequenceNum, errorMsg,
          });
        } else {
          eventBus.emit({
            type:        "compute:sync_failed",
            userId,
            sequenceNum: entry.sequenceNum,
            error:       errorMsg,
            attempt:     attempt + 1,
            nextRetryMs: nextDelay,
          });
          logger.warn("ACUPersistenceBridge: sync failed, retrying", {
            userId, sequenceNum: entry.sequenceNum, attempt: attempt + 1, nextDelay, errorMsg,
          });
          await sleep(nextDelay);
          if (token !== this.sessionToken) return; // session changed during sleep
        }
      }
    }
  }

  /* ── CRASH RECOVERY ─────────────────────────────────────────────── */

  private async replayUnsynced(token: number): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (token !== this.sessionToken) return; // session changed during auth fetch
    if (!user) {
      logger.debug("ACUPersistenceBridge: no auth user - skipping unsynced replay");
      return;
    }

    const unsynced = ACUOfflineJournal.getAllUnsynced(user.id);
    if (unsynced.length === 0) return;

    logger.info("ACUPersistenceBridge: replaying unsynced journal entries", {
      userId: user.id,
      count: unsynced.length,
    });

    // Re-enqueue unsynced entries in order (oldest first - sorted by savedAt)
    const sorted = [...unsynced].sort((a, b) => a.savedAt - b.savedAt);
    this.syncQueue.push(...sorted);
    void this.drainSyncQueue(user.id, token);
  }
}

/* =========================================================
   SINGLETON + RUNTIME MODULE EXPORT
   ========================================================= */

const bridge = new ACUPersistenceBridgeImpl();

/** RuntimeModule definition for coreRuntime registration. */
export const acuPersistenceModule: RuntimeModule = {
  id:           "acu-persistence",
  name:         "ACU Persistence Bridge",
  dependencies: ["persistence", "state", "diagnostics"],
  init:         () => bridge.init(),
  teardown:     () => bridge.teardown(),
};
